// Amount and text formatting. All amount math is string/BigInt based: never floating point.
// Dates follow the console language (Intl); words come from the active dictionary (src/i18n).
import { fmt, intlLocale, tr } from "../i18n/index.tsx";

const group = (int: string) => int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * Integer string in smallest units → human string, e.g. ("1234500000", 6) → "1,234.50".
 * Trailing zeros are trimmed but at least `minFrac` decimals are kept (default 2).
 * Display stops at 2 decimals, rounding down so a balance is never shown higher than it is.
 * Pass `maxFrac = decimals` (or use exactUnits) for exact values: inputs, pay amounts, hover titles.
 */
export function formatUnits(raw: string | null | undefined, decimals: number, minFrac = 2, maxFrac = 2): string {
  if (raw === null || raw === undefined || raw === "") return "–";
  let s = String(raw).trim();
  let neg = false;
  if (s.startsWith("-")) {
    neg = true;
    s = s.slice(1);
  }
  if (!/^\d+$/.test(s)) return String(raw);
  s = s.replace(/^0+(?=\d)/, "");
  const padded = s.padStart(decimals + 1, "0");
  const int = decimals > 0 ? padded.slice(0, -decimals) : padded;
  let frac = decimals > 0 ? padded.slice(-decimals).slice(0, maxFrac) : "";
  frac = frac.replace(/0+$/, "");
  const keep = Math.min(minFrac, decimals);
  if (frac.length < keep) frac = frac.padEnd(keep, "0");
  return (neg ? "-" : "") + group(int) + (frac ? `.${frac}` : "");
}

/** Every decimal kept, for hover titles next to 2-decimal amounts. */
export const exactUnits = (raw: string | null | undefined, decimals: number) => formatUnits(raw, decimals, 2, decimals);

export const formatAmount = (raw: string | null | undefined, decimals: number, token?: string) => {
  const v = formatUnits(raw, decimals);
  return token && v !== "–" ? `${v} ${token}` : v;
};

/**
 * Human amount → smallest-unit integer string. Returns null when the input isn't a valid
 * non-negative number with at most `decimals` fraction digits. Commas and spaces are ignored.
 */
export function parseUnits(input: string, decimals: number): string | null {
  const s = input.replace(/[,\s_]/g, "");
  if (!/^\d*(\.\d*)?$/.test(s) || s === "" || s === ".") return null;
  const [int = "", frac = ""] = s.split(".");
  if (frac.length > decimals) return null;
  const out = ((int || "0") + frac.padEnd(decimals, "0")).replace(/^0+(?=\d)/, "");
  return out;
}

/** Fraction digits in a human amount input (for "too many decimals" hints). */
export const fractionDigits = (input: string) => (input.split(".")[1] ?? "").length;

/** Decimal USD string ("12345.678") → "$12,345.68" (rounded half-up, string math). */
export function formatUsd(v: string | null | undefined, opts: { compact?: boolean } = {}): string {
  if (v === null || v === undefined || v === "") return "–";
  const m = /^(-?)(\d*)(?:\.(\d*))?$/.exec(String(v).trim());
  if (!m) return String(v);
  const [, sign, intPart = "0", fracPart = ""] = m;
  let cents = BigInt((intPart || "0") + (fracPart + "00").slice(0, 2));
  if ((fracPart[2] ?? "0") >= "5") cents += 1n;
  if (opts.compact) {
    const dollars = Number(cents / 100n);
    if (dollars >= 1_000_000) return `${sign}$${(dollars / 1_000_000).toFixed(dollars >= 10_000_000 ? 0 : 1).replace(/\.0$/, "")}M`;
    if (dollars >= 1_000) return `${sign}$${(dollars / 1_000).toFixed(dollars >= 10_000 ? 0 : 1).replace(/\.0$/, "")}K`;
  }
  const s = cents.toString().padStart(3, "0");
  return `${sign}$${group(s.slice(0, -2))}.${s.slice(-2)}`;
}

/** Number of USD for chart scaling only (display values still use formatUsd). */
export const usdNumber = (v: string) => Number.parseFloat(v) || 0;

/** "TQ9x…7kLm" / "0x5f3a…9c2e": head and tail kept, full value goes in a title attribute. */
export function shorten(v: string | null | undefined, head?: number, tail = 4): string {
  if (!v) return "–";
  head ??= v.startsWith("0x") ? 6 : 4;
  if (v.length <= head + tail + 1) return v;
  return `${v.slice(0, head)}…${v.slice(-tail)}`;
}

type DateOpts = Intl.DateTimeFormatOptions;
const SHORT: DateOpts = { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false };
const FULL: DateOpts = { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false };
const cache = new Map<string, Intl.DateTimeFormat>();
/** Date formatter for the console language (English uses en-GB: "10 Oct, 14:32"). */
function df(opts: DateOpts): Intl.DateTimeFormat {
  const loc = intlLocale() === "en" ? "en-GB" : intlLocale();
  const key = loc + JSON.stringify(opts);
  let f = cache.get(key);
  if (!f) cache.set(key, (f = new Intl.DateTimeFormat(loc, opts)));
  return f;
}
const tidy = (s: string) => s.replace("Sept", "Sep");
/** "10 Oct 2026, 14:32:05 UTC" (for title attributes next to local times). */
export const formatUtc = (iso: string | null | undefined) => (iso ? `${tidy(df({ ...FULL, timeZone: "UTC" }).format(new Date(iso)))} UTC` : "");

/** Compact local time for tables: "10 Oct, 14:32". */
export const formatTime = (iso: string | null | undefined) => (iso ? tidy(df(SHORT).format(new Date(iso))) : "–");
/** Full local time with year and seconds for detail views. */
export const formatTimeFull = (iso: string | null | undefined) => (iso ? tidy(df(FULL).format(new Date(iso))) : "–");
/** "10 Oct" from a YYYY-MM-DD (UTC) date. */
export const formatDay = (ymd: string) => tidy(df({ day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${ymd}T00:00:00Z`)));

/** Today's UTC date as YYYY-MM-DD, shifted by `days`. */
export function utcDay(days = 0): string {
  return new Date(Date.now() + days * 86_400_000).toISOString().slice(0, 10);
}

export function timeAgo(iso: string | null | undefined): string {
  const c = tr().common;
  if (!iso) return c.never;
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 0) return fmt(c.inTime, { t: timeAgoAbs(-s) });
  if (s < 45) return c.justNow;
  return fmt(c.ago, { t: timeAgoAbs(s) });
}
function timeAgoAbs(s: number) {
  const c = tr().common;
  if (s < 3600) return fmt(c.minutes, { n: Math.max(1, Math.round(s / 60)) });
  if (s < 86400) return fmt(c.hours, { n: Math.round(s / 3600) });
  return fmt(c.days, { n: Math.round(s / 86400) });
}

export const CHAIN_NAMES: Record<string, string> = {
  tron: "TRON", bsc: "BNB Chain", eth: "Ethereum", arbitrum: "Arbitrum", base: "Base", polygon: "Polygon", solana: "Solana",
};
export const chainName = (c: string) => CHAIN_NAMES[c] ?? c.toUpperCase();
/** "tron:USDT" → "USDT". */
export const tokenOf = (asset: string) => asset.split(":")[1] ?? asset;
export const chainOf = (asset: string) => asset.split(":")[0] ?? asset;
export const assetLabel = (asset: string) => `${tokenOf(asset)} · ${chainName(chainOf(asset))}`;

/** Read-through views of the active dictionary, keyed by API code (unknown codes give undefined). */
const view = (pick: () => Record<string, string>) =>
  new Proxy({} as Record<string, string>, { get: (_, k) => (typeof k === "string" ? pick()[k] : undefined) });

export const STATUS_LABEL = view(() => tr().common.statuses);
export const statusLabel = (s: string) => STATUS_LABEL[s] ?? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, " ");

export const ROLE_LABEL = view(() => tr().common.roles);
export const ROLE_DESC = view(() => tr().common.roleDesc);
